import { Box, Button, Typography } from '@mui/material'
import React from 'react'

import Database from '../../types/Database'
import { ListDesks } from '../desk'

interface DeskAvailabilityProps {
  desks: Database.DeskWithAvailability[]
  selectedDesks: Database.DeskWithAvailability[]
  uid?: string
  onDeskClicked: (desk: Database.DeskWithAvailability) => void
  officeName?: string
  busy?: boolean
  onBookSelected: () => void
  bookings?: Database.Booking[]
  dateRange?: Date[]
}

/**
 * DeskAvailability - Component for displaying desk availability
 *
 * Shows available and unavailable desks for the selected date range
 * with appropriate counts and selection handling.
 */
export default function DeskAvailability({
  desks,
  selectedDesks,
  uid,
  onDeskClicked,
  officeName: _officeName,
  busy = false,
  onBookSelected,
  bookings,
  dateRange
}: DeskAvailabilityProps) {
  const availableCount = desks.filter(d => d.isAvailable).length
  const unavailableCount = desks.filter(d => !d.isAvailable).length

  if (desks.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          backgroundColor: theme =>
            theme.palette.mode === 'dark'
              ? 'rgba(30, 41, 59, 0.5)'
              : 'rgba(0, 0, 0, 0.03)',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          maxWidth: 1000,
          mx: 'auto'
        }}
      >
        <Typography variant='body2' color='text.secondary'>
          No desks found for this office.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', width: '100%', mt: -1 }}>
      <Box
        sx={{
          backgroundColor: theme =>
            theme.palette.mode === 'dark'
              ? 'rgba(30, 41, 59, 0.5)'
              : 'rgba(0, 0, 0, 0.03)',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant='h6' sx={{ fontWeight: 600, mb: 0.5 }}>
            Available Desks
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {availableCount} available, {unavailableCount} unavailable for
            selected dates
          </Typography>
        </Box>

        <Box sx={{ p: 0 }}>
          <ListDesks
            desks={desks}
            uid={uid}
            onDeskClicked={onDeskClicked}
            onDeskDeleted={null}
            onDeskEdit={null}
            selectedDesks={selectedDesks}
            readOnly={false}
            title=''
            bookings={bookings}
            dateRange={dateRange}
          />
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            textAlign: 'center'
          }}
        >
          <Button
            variant='contained'
            onClick={onBookSelected}
            disabled={busy || selectedDesks.length === 0}
            size='medium'
            sx={{
              px: 3,
              py: 1.25,
              fontWeight: 600,
              borderRadius: 2,
              minWidth: 120
            }}
          >
            Complete Booking
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
